import { useState } from "react";
import type { Hero } from "@/types/cloud-data";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getItemName } from "@/data/item-names";

interface HeroesTabProps {
  heroes: Hero[];
  onEditHero: (heroIndex: number, field: string, value: string) => void;
}

const HERO_NAMES: Record<number, string> = {
  1: "Claris", 2: "Chase", 3: "Amy", 4: "Alaster",
  5: "Edna", 6: "Louisa", 7: "Elze", 8: "Zerav",
};

function getHeroName(hero: Hero): string {
  const id = (hero as Record<string, unknown>).HERO as number ?? 0;
  return HERO_NAMES[id] ?? `Hero ${id}`;
}

const RARITY_COLORS: Record<number, string> = {
  0: "#FFFFFF",
  1: "#9EFF9E",
  2: "#66B2FF",
  3: "#FF786E",
  4: "#FF8CFF",
};

const STAT_ABBR: Record<number, string> = {
  0:  "—",
  1:  "ATK",
  2:  "DEF",
  3:  "ATK SPD",
  4:  "CRIT",
  5:  "CRIT DMG",
  6:  "HP%",
  7:  "REC",
  8:  "ELEM RES",
  9:  "PHY RES",
  10: "EVA",
  11: "SPC",
  12: "MPR",
  13: "CDR",
  14: "MAX HP",
  15: "MAX MP",
  16: "B.ATK",
  17: "S.ATK",
  18: "ACC",
  19: "MANA GN",
  20: "SP REGEN",
  21: "CON+",
  22: "ATK%",
  23: "DEF%",
};

function itemColor(rarity: number): string {
  return RARITY_COLORS[rarity] ?? "#FFFFFF";
}

function formatStats(item: { randomStat?: { stats: number; value: number } | null; fixedStats?: { stats: number; value: number }[]; socketStats?: { socketID: string; currentLevel: number }[] }): string {
  const parts: string[] = [];
  if (item.randomStat) {
    const abbr = STAT_ABBR[item.randomStat.stats] ?? `S${item.randomStat.stats}`;
    parts.push(`${abbr}:${(item.randomStat.value / 10).toFixed(1)}%`);
  }
  if (item.fixedStats?.length) {
    const sorted = [...item.fixedStats].sort((a, b) => a.stats - b.stats);
    for (const f of sorted) {
      const abbr = STAT_ABBR[f.stats] ?? `S${f.stats}`;
      parts.push(`${abbr}:${(f.value / 10).toFixed(1)}%`);
    }
  }
  if (item.socketStats?.length) {
    for (const s of item.socketStats) {
      const rName = getItemName(s.socketID);
      parts.push(rName ? `${rName}+${s.currentLevel}` : `Rune:${s.socketID}+${s.currentLevel}`);
    }
  }
  return parts.join(" | ") || "—";
}

function HeroRow({ hero, index, isSelected, onClick }: {
  hero: Hero; index: number; isSelected: boolean; onClick: () => void;
}) {
  const eqCount = hero.equipment?.filter(e => e.id).length ?? 0;
  return (
    <tr
      className={`cursor-pointer transition-colors ${isSelected ? "bg-primary/10" : "hover:bg-muted/50"}`}
      onClick={onClick}
    >
      <td className="px-3 py-2 text-xs font-mono text-muted-foreground">#{index + 1}</td>
      <td className="px-3 py-2">
        <Badge variant={isSelected ? "default" : "secondary"} className="text-xs">
          {getHeroName(hero)} Lv {hero.LEVEL}
        </Badge>
      </td>
      <td className="px-3 py-2 text-xs font-mono">{(hero.ATK ?? 0).toFixed(0)}</td>
      <td className="px-3 py-2 text-xs font-mono">{(hero.HP ?? 0).toFixed(0)}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{hero.STR}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{hero.INT}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{hero.DEX}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{hero.AGI}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{hero.VIT}</td>
      <td className="px-3 py-2">
        <Badge variant="outline" className="text-xs">{eqCount} EQ</Badge>
      </td>
    </tr>
  );
}

function EquipmentView({ hero, onBack }: { hero: Hero; onBack: () => void }) {
  const equipment = hero.equipment ?? [];
  const validEquipment = equipment.filter(e => e.id);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onBack}>← Back</Button>
          <span className="text-sm font-medium">{getHeroName(hero)} Lv {hero.LEVEL} — Equipment</span>
        </div>
        <Badge variant="secondary" className="text-xs">{validEquipment.length} items</Badge>
      </div>
      <div className="rounded border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {validEquipment.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-muted-foreground text-xs">No equipment</td>
              </tr>
            ) : validEquipment.map((eq, i) => {
              const name = getItemName(eq.id);
              const color = itemColor(eq.currentRarity ?? 0);
              const label = eq.enhancementLevel > 0 ? `${name} +${eq.enhancementLevel}` : name;
              return (
                <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                  <td className="px-3 py-2 text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2 text-sm font-medium"><span style={{ color }}>{label}</span></td>
                  <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{formatStats(eq)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function HeroesTab({ heroes }: HeroesTabProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (selectedIndex !== null) {
    const hero = heroes[selectedIndex];
    return (
      <EquipmentView
        hero={hero}
        onBack={() => setSelectedIndex(null)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{heroes.length} heroes</span>
      </div>
      <div className="rounded border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Level</th>
              <th className="px-3 py-2 text-left">ATK</th>
              <th className="px-3 py-2 text-left">HP</th>
              <th className="px-3 py-2 text-left">STR</th>
              <th className="px-3 py-2 text-left">INT</th>
              <th className="px-3 py-2 text-left">DEX</th>
              <th className="px-3 py-2 text-left">AGI</th>
              <th className="px-3 py-2 text-left">VIT</th>
              <th className="px-3 py-2 text-left">EQ</th>
            </tr>
          </thead>
          <tbody>
            {heroes.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-muted-foreground text-xs">No heroes</td>
              </tr>
            ) : heroes.map((hero, i) => (
              <HeroRow
                key={i}
                hero={hero}
                index={i}
                isSelected={selectedIndex === i}
                onClick={() => setSelectedIndex(i)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
